import { HeaderCard } from '@data/components/composite/HeaderCard/HeaderCard';
import { Metrics } from '@data/components/composite/Metrics/Metrics';
import {
  useMetricsV2Api,
  type MetricV2Response,
} from '@data/hooks/api/use-metrics-v2-api/use-metrics-v2-api';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import { format } from 'date-fns';
import { useGetMeApi } from 'eds-common/api/v2';
import { useMemo } from 'react';
import { ProductCard } from '@data/components/composite/ProductCard/ProductCard';
import { isSupportedProduct } from '@data/config/reports';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';
import { getGradeDisplay } from '@data/utils/get-grade-range';
import { getEdApps } from '@data/utils/get-ed-apps';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface PartialMetricV2Request {
  orgId: string;
  teacherIds: string[];
  startDate: Date;
  endDate: Date;
  classIds?: string[] | null;
  studentIds?: string[] | null;
  enabled?: boolean;
}

export function ProductLandingPage() {
  const { data: user } = useGetMeApi();
  const { data: applications } = useApplications();
  const { dndShowRise } = useFlags();

  const supportedApps = applications?.filter(
    (app) =>
      isSupportedProduct(app.applicationCode) &&
      // Check if feature flag is false and omit RISE products
      !(!dndShowRise && app.applicationCode === 'rise')
  );

  const appCodes = useMemo(
    () => supportedApps?.map((app) => app.applicationCode) ?? [],
    [supportedApps]
  );

  const { from: startDate, to: endDate } = useMemo(
    () => getSchoolYearDateRange(),
    []
  );

  const metricsEnabled = !!user?.userId && !!user?.currentOrg?.orgId;

  const sharedMetricProps = useMemo(
    () => ({
      orgId: user?.currentOrg?.orgId ?? '',
      teacherIds: [user?.userId ?? ''],
      startDate,
      endDate,
      enabled: metricsEnabled,
    }),
    [user?.currentOrg?.orgId, user?.userId, startDate, endDate, metricsEnabled]
  );

  const {
    data: sessionsData,
    isSuccess: sessionsReady,
    isLoading: sessionsLoading,
  } = useMetricsV2Api({
    ...sharedMetricProps,
    edApps: getEdApps(appCodes),
    metricType: 'TOTAL_STUDENT_SESSIONS',
  });

  const {
    data: minutesData,
    isSuccess: minutesReady,
    isLoading: minutesLoading,
  } = useMetricsV2Api({
    ...sharedMetricProps,
    edApps: getEdApps(appCodes),
    metricType: 'TOTAL_STUDENT_MINUTES',
  });

  const productCardsEnabled = sessionsReady && minutesReady;
  const metricsLoading = sessionsLoading || minutesLoading;

  const metrics: MetricV2Response[] = useMemo(
    () => [sessionsData, minutesData].filter((m): m is MetricV2Response => !!m),
    [sessionsData, minutesData]
  );

  return (
    <div className="flex-1 bg-brand-grey_7 antialiased">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col py-6 mobile:px-4 tablet:px-10">
        <HeaderCard>
          <h2>Data and Insights</h2>
        </HeaderCard>
        {applications && supportedApps?.length === 0 ? (
          <div className="rounded-lg border-[1px] border-dashed border-neutral-400 px-6 py-8 tablet:mt-7 tablet:p-10">
            <h3 className="text-xl font-bold tablet:text-2xl">
              No supported products yet
            </h3>
            <p className="mt-2 text-sm text-neutral-1300 tablet:text-lg">
              Data &amp; Insights are unavailable for your current products.
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-semibold">Highlights</h2>
            <p className="mt-3 pb-3 text-sm text-neutral-1300">
              Student data for {format(startDate, 'MM/dd/yyyy')} -{' '}
              {format(endDate, 'MM/dd/yyyy')}
            </p>
            <Metrics metrics={metrics} isLoading={metricsLoading} count={2} />
            <h2 className="mb-4 mt-8 text-2xl font-semibold">
              Available Products
              {supportedApps ? ` (${supportedApps.length})` : ''}
            </h2>
            <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 tablet:gap-8 laptop:grid-cols-3">
              {supportedApps?.map((app) => {
                return (
                  <ProductCard
                    key={app.applicationCode}
                    applicationCode={app.applicationCode}
                    applicationLabel={app.applicationLabel}
                    applicationIconSrc={app.applicationIconSrc}
                    gradeDisplay={getGradeDisplay(app)}
                    sharedMetricProps={{
                      ...sharedMetricProps,
                      enabled: productCardsEnabled,
                    }}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
